import React, { useContext, useEffect, useState } from 'react'
import { FormProvider, SubmitErrorHandler, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { AircraftDetail, FLIGHT_DETAIL_DEFAULTS, FlightDetail, FlightDetailInput } from 'types/techlog'
import { DisplayModus, SAISIE_HORAIRE } from 'constants/constants'
import { useNavigate } from 'react-router-dom'
import LocalStorage from 'utils/myLocalStorage'
import ComponentsUNames from 'constants/componentsUNames'
import FormInput from 'components/FormFields/FormInput'
import FormDate from 'components/FormFields/FormDate'
import FormSelect from 'components/FormFields/FormSelect'
import { isValid, parse } from 'date-fns'
import FormTime from 'components/FormFields/FormTime'
import { Message, MessageSeverity } from 'constants/message'
import useOptions from 'utils/hooks/useOptions'
import URL_APIS from 'utils/api/urlApis'
import defaults from 'lodash.defaults'
import {
  Button,
  Col,
  ComboBoxDefaultOption,
  IconButton,
  Inline,
  Row,
  Stack,
  Step,
  StepHeader,
  Stepper,
  Typography,
} from '@airbus/components-react/components'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faSquarePlus } from '@fortawesome/free-regular-svg-icons'
import { Workorder } from 'types/workorder/workorder'
import { Lang } from 'context/LangContext'
import FormToggle from 'components/FormFields/FormToggle'
import FormNumericInput from 'components/FormFields/FormNumericInput'
import useMutation from 'utils/hooks/useMutation'
import TechlogFuels, { SelectedFuelDetail } from './TechlogFuels'
import MessageContext from 'context/MessageContext'
import tools from 'utils/helpers'
import { manageFormErrors } from 'components/FormFields/FormField'
import Fieldset from 'components/Fieldset/fieldset'
import ModalTechlogFuel from './modalTechlogFuel'
import TechlogWorkorders from './TechlogWorkorders'
import ModalConfirm from 'components/Modal/ModalConfim'
import NonStandardAgeingDetails from './NonStandardAgeingDetails'
import LoadingContext from 'context/LoadingContext'
import useQuery from 'utils/hooks/useQuery'

const DEFAULT_CODE_LENGTH = 3
export const renderOptionLabel = (o: ComboBoxDefaultOption, codeLength: number = DEFAULT_CODE_LENGTH) => o?.label

// Durations are to be displayed in format H,cc or HH:mm (default HH:mm)
const formatDuration = LocalStorage.getOrDefault(
  [LocalStorage.KEY_ROUTINE_MAINTENANCE_PARAMS, ComponentsUNames.RMP_SAISIE_HORAIRE],
  LocalStorage.VALUE_TYPE.STRING,
  SAISIE_HORAIRE.HEURES_MINUTES,
)

// List mapping fields in error after a submission returning errors/warnings
const messagesErrWarnFromBack = {
  SRM4101: {
    //message: "Aéroport arrivée doit être le même que celui du départ",
    severity: MessageSeverity.ERROR,
    fieldNames: ['voaerodep', 'voaeroarr'],
  },
  SRM4129: {
    //message: "Heure départ A/B doit être > heure arrivée A/B vol précédent",
    severity: MessageSeverity.ERROR,
    fieldNames: ['voabdep'],
  },
  SRM4128: {
    //message: "Heure de départ B/B doit être > heure arrivée B/B précédent",
    severity: MessageSeverity.ERROR,
    fieldNames: ['vobbdep'],
  },
  SRM4186: {
    //message: "Type de vol inconnu",
    severity: MessageSeverity.ERROR,
    fieldNames: ['votycod'],
  },
  SRM1405: {
    //message:"Type de vol/mission n'est pas actif ",
    severity: MessageSeverity.ERROR,
    fieldNames: ['votycod'],
  },
  SRM1400: {
    //message: "Type de vol/mission n'est pas lié à l'applicabilité",
    severity: MessageSeverity.ERROR,
    fieldNames: ['votycod'],
  },
  SRM4114: {
    //message: "Code retard inconnu",
    severity: MessageSeverity.ERROR,
    fieldNames: ['vortdcod1', 'vortdcod2'],
  },
  SOE0031: {
    //message: "Attention A/C non ETOPS",
    severity: MessageSeverity.ERROR,
    fieldNames: ['voetops'],
  },
  SRM4117: {
    //message: "Durée Airborne nulle - pas de Touch&Go possible",
    severity: MessageSeverity.ERROR,
    fieldNames: ['votogo'],
  },
  SRM4116: {
    //message: "Durée A/B doit être < durée B/B",
    severity: MessageSeverity.ERROR,
    fieldNames:
      formatDuration === SAISIE_HORAIRE.HEURES_CENTIEMES ? ['voabtpsc', 'vobbtpsc'] : ['voabhhmm', 'vobbhhmm'],
  },
  GEN8211: {
    //message: "Date doit être renseignée",
    severity: MessageSeverity.ERROR,
    fieldNames: ['_vodate'],
  },
  SRM4202: {
    //message: "Date CRM erronée",
    severity: MessageSeverity.ERROR,
    fieldNames: ['_vodate'],
  },
  SRM1011: {
    //message: "Date CRM ne peut être > date du jour",
    severity: MessageSeverity.ERROR,
    fieldNames: ['_vodate'],
  },
  SRM4145: {
    //message: "Date du CRM est inférieure à celle du dernier connu",
    severity: MessageSeverity.ERROR,
    fieldNames: ['_vodate'],
  },
  SRM4105: {
    //message: "Aeroport doit etre renseigné",
    severity: MessageSeverity.ERROR,
    fieldNames: ['voaerodep', 'voaeroarr'],
  },
  SRM4104: {
    //message: "Aéroport différent du dernier touché",
    severity: MessageSeverity.ERROR,
    fieldNames: ['voaerodep'],
  },
  SRM0227: {
    //message: "OK, mais Heure de départ B/B > Heure de Départ A/B",
    severity: MessageSeverity.WARNING,
    fieldNames: ['vobbdep', 'voabdep'],
  },
  SRM0228: {
    //message: "OK, mais Heure d'arrivée A/B > Heure d'arrivée B/B",
    severity: MessageSeverity.WARNING,
    fieldNames: ['vobbarr', 'voabarr'],
  },
  SRM4130: {
    //message: "Heure sous la forme HHMM incorrecte",
    severity: MessageSeverity.ERROR,
    fieldNames: ['voabdep', 'vobbdep', 'voabarr', 'vobbarr'],
  },
  SRM4120: {
    //message: "Durée de vol incompatible et heures incompatibles",
    severity: MessageSeverity.ERROR,
    fieldNames:
      formatDuration === SAISIE_HORAIRE.HEURES_CENTIEMES
        ? ['voabtpsc', 'vobbtpsc', 'voabdep', 'vobbdep', 'voabarr', 'vobbarr']
        : ['voabhhmm', 'vobbhhmm', 'voabdep', 'vobbdep', 'voabarr', 'vobbarr'],
  },
  HEU1010: {
    //message: "Format horaire incorrect",
    severity: MessageSeverity.ERROR,
    fieldNames: ['voabdep', 'vobbdep', 'voabarr', 'vobbarr'],
  },
  SRM0450: {
    //message: "Attention, temps > 24h",
    severity: MessageSeverity.ERROR,
    fieldNames: [
      'vocpt1',
      'vocpt2',
      'vocpt3',
      'vocpt4',
      'vocpt5',
      'vocpt6',
      'vocpt7',
      'vocpt8',
      'vocpt9',
      'vocpt10',
      'vocpt11',
      'vocpt12',
      'vocpt13',
      'vocpt14',
      'vocpt15',
      'vocpt16',
      'vocpt17',
      'vocpt18',
      'vocpt19',
      'vocpt20',
    ],
  },
}

type Props = {
  lang: Lang
  displayMode: DisplayModus
  aircraft?: AircraftDetail
  lastCrm?: string
  flightDetail?: FlightDetail
  onClose: () => void
  onActionCompleted: () => void
}

const TechlogForm = ({ lang, displayMode, aircraft, lastCrm, flightDetail, onClose, onActionCompleted }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { handleDisplayMessage } = useContext(MessageContext)
  const { isLoading } = useContext(LoadingContext)

  const [workorders, setWorkorders] = useState<Workorder[]>([])
  const [modalFuelOpened, setModalFuelOpened] = useState<boolean>(false)
  const [modalFormFuelDisplayMode, setModalFormFuelDisplayMode] = useState<DisplayModus>(DisplayModus.READ)
  const [selectedTechlogFuel, setSelectedTechlogFuel] = useState<SelectedFuelDetail>()
  const [isConfirmOpened, setIsConfirmOpened] = useState(false)

  const [fuelsDetail, setFuelsDetail] = useState<SelectedFuelDetail[]>([])
  const { fetchData: fetchFuelsDetail } = useQuery<SelectedFuelDetail[]>(URL_APIS.URL_CRM_FUELS_GET)

  const { nonStandardAgeingDetails, avnbapu: nbApu, avnbmot: nbEngines, avnbhyd: nbHydro } = aircraft || {}

  const [activeStep, setActiveStep] = useState(0)
  const steps = [t('stepper_crm_step_0'), t('stepper_crm_step_1'), t('stepper_crm_step_2')]

  const creating = !flightDetail?.rmdoc
  const readOnly = displayMode !== DisplayModus.WRITE && displayMode !== DisplayModus.CREATE
  const editing = flightDetail && displayMode === DisplayModus.WRITE
  const isLast = flightDetail?.rmdoc === lastCrm

  const canUpdate = LocalStorage.getOrDefault(
    [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.CRM_UPDATE_PERMISSIONS],
    LocalStorage.VALUE_TYPE.BOOLEAN,
    false,
  )
  // Can user input data for departures/arrivals
  const canInputDepArr = LocalStorage.getOrDefault(
    [LocalStorage.KEY_ROUTINE_MAINTENANCE_PARAMS, ComponentsUNames.RMP_CRM_INPUT_DEP_ARR],
    LocalStorage.VALUE_TYPE.BOOLEAN,
    false,
  )
  // Can user input data for BB
  const canInputBB = LocalStorage.getOrDefault(
    [LocalStorage.KEY_ROUTINE_MAINTENANCE_PARAMS, ComponentsUNames.RMP_CRM_INPUT_BB],
    LocalStorage.VALUE_TYPE.BOOLEAN,
    false,
  )
  // User is allowed to add/remove fuel details only if fuel unit has been configured in Amasis by an Administrator
  const canManageFuel =
    LocalStorage.getOrDefault(
      [LocalStorage.KEY_ROUTINE_MAINTENANCE_PARAMS, ComponentsUNames.RMP_FUEL_UNIT],
      LocalStorage.VALUE_TYPE.STRING,
      undefined,
    ) !== undefined
  // Can user see/update Fuel totals
  const canViewTotals = LocalStorage.getOrDefault(
    [LocalStorage.KEY_ROUTINE_MAINTENANCE_PARAMS, ComponentsUNames.RMP_CRM_DISPLAY_TOTALS],
    LocalStorage.VALUE_TYPE.BOOLEAN,
    false,
  )
  const canCreateWorkorder = LocalStorage.getOrDefault(
    [LocalStorage.KEY_PERMISSIONS, ComponentsUNames.WORKORDER_CREATE_PERMISSIONS],
    LocalStorage.VALUE_TYPE.BOOLEAN,
    false,
  )

  const { fetchData: fetchWorkorders } = useQuery<Workorder[]>(URL_APIS.URL_CRM_WORKORDERS_GET_PARAMETERIZED)
  const { mutate: createOrUpdateFlightDetails } = useMutation<FlightDetailInput>(
    URL_APIS.URL_CRM_BYFLIGHT_CREATE_POST,
    '',
    messagesErrWarnFromBack,
    true,
  )

  const { loadOptions: loadOptionsFlightTypes, options: flightTypeOptions } = useOptions(
    URL_APIS.URL_CRM_FLIGHT_TYPES_GET,
  )
  const { loadOptions: loadOptionsDelayCodes, options: delayCodeOptions } = useOptions(URL_APIS.URL_CRM_DELAY_CODES_GET)

  /**
   * NSAD in format Numerical are always retrieved (GET) with 2 decimals whatever 0 or more decimals are expected
   * But while sending back data, we must ste the right number of decimals or none if requested.
   * Hence this conversion
   * @param fd
   * @returns
   */
  const formatNonStandardAgeingDetails = (fd: FlightDetailInput) => {
    const allNsadFormatted = {}
    if (fd && aircraft) {
      for (let i = 1; i <= 20; i++) {
        if (aircraft.nonStandardAgeingDetails[i]?.vitytyp === 'N') {
          const nsad = fd['vocpt' + i]
          let nsadFormattedValue = nsad
          if (nsad != undefined) {
            const nsadParts = nsad.split('.')
            if (nsadParts.length === 2) {
              // Int and decimal parts present
              const expectedNbDec = aircraft?.nonStandardAgeingDetails[i]?.vitydec as number
              if (expectedNbDec === 0) {
                nsadFormattedValue = nsadParts[0] as string
              } else if (expectedNbDec <= nsadParts[1].length) {
                nsadFormattedValue = nsadParts[0] + '.' + nsadParts[1].substring(0, expectedNbDec)
              } else {
                // Complete with trailing zeroes
                nsadFormattedValue =
                  nsadParts[0] + '.' + nsadParts[1] + '000000000'.slice(nsadParts[1].length - expectedNbDec)
              }
            }
            fd['vocpt' + i] = nsadFormattedValue
          }
        }
        allNsadFormatted[i as string] = fd['vocpt' + i]
      }
    }
    return allNsadFormatted
  }

  const methods = useForm<FlightDetailInput>({
    defaultValues: FLIGHT_DETAIL_DEFAULTS,
    values: {
      ...defaults(flightDetail, FLIGHT_DETAIL_DEFAULTS),
      ...formatNonStandardAgeingDetails(flightDetail),
      _rmdate:
        (flightDetail?.rma4 &&
          flightDetail?.rmmm &&
          flightDetail?.rmjj &&
          new Date(parseInt(flightDetail.rma4), parseInt(flightDetail.rmmm) - 1, parseInt(flightDetail.rmjj))) ||
        null,
      vobbhhmm: tools.convertMinutesToHhmm(flightDetail?.vobbtps),
      voabhhmm: tools.convertMinutesToHhmm(flightDetail?.voabtps),
    },
  })

  const {
    handleSubmit,
    watch,
    getValues,
    formState: { errors, isDirty },
  } = methods

  /**
   *  Performs a data validation on step 0
   * @param stepIndex
   */
  const handleGotoStep = async (stepIndex: number) => {
    if (activeStep === 0) {
      const validationHandler = handleSubmit((data) => {
        setActiveStep(stepIndex)
      })()
      await validationHandler
      if (Object.keys(errors).length > 0) {
        handleDisplayMessage(
          new Message(
            MessageSeverity.WARNING,
            rmdoc ? t('9500159') : t('9500160'),
            '',
            t('input_compulsory_fields_missing'),
          ),
        )
      }
    } else setActiveStep(stepIndex)
  }

  const onError: SubmitErrorHandler<FlightDetailInput> = (errors) => {
    console.debug({ errors })
    manageFormErrors(errors, (s: string, t: string) => handleDisplayMessage(new Message(s, '', null, t)))
  }

  const onSubmit = async (data: FlightDetailInput) => {
    const rma4 = '' + data._rmdate?.getFullYear()
    const rmmm =
      data._rmdate?.getMonth() !== undefined && data._rmdate?.getMonth() !== null
        ? ('0' + (data._rmdate?.getMonth() + 1).toString()).slice(-2)
        : ''
    const rmjj =
      data._rmdate?.getDate() !== undefined && data._rmdate?.getDate() !== null
        ? ('0' + data._rmdate?.getDate().toString()).slice(-2)
        : ''

    await createOrUpdateFlightDetails({
      data: {
        ...data,
        appl: data.appl || aircraft?.appl,
        voord: 1,
        rma4: rma4,
        rmmm: rmmm,
        rmjj: rmjj,
        vovolnum: data.vovolnum && data.vovolnum.toUpperCase(),
        voaeroarr: data.voaeroarr && data.voaeroarr.toUpperCase(),
        vobbtps: tools.convertHhmmToInt(data.vobbhhmm),
        voabtps: tools.convertHhmmToInt(data.voabhhmm),
        _rmdate: null,
      },
      params: { lang },
      method: displayMode === DisplayModus.CREATE ? 'POST' : 'PUT',
    })
    onActionCompleted()
  }

  const onFuelsChange = async () => {
    const rmDateArray = getValues('rmdate') as number[]
    const fuelsDetailResponse = await fetchFuelsDetail({
      lang: lang,
      avnumap: flightDetail?.avnumap || null,
      rmdate: rmDateArray
        ? tools.convertDatePformatToYyyySepMmSepJj(tools.convertArrayDateToPformat(rmDateArray), '-')
        : null,
      rmdoc: flightDetail?.rmdoc,
    })
    setFuelsDetail(fuelsDetailResponse?.data || [])
  }

  const handleModalFormFuelPostSubmit = () => {
    setModalFuelOpened(false)
    setSelectedTechlogFuel(undefined)
    onFuelsChange()
  }

  const handleCreateWorkOrder = () => {
    navigate('new')
  }

  const handleConfirmSubmit = () => {
    setIsConfirmOpened(false)
    onClose()
  }

  const handleClose = () => {
    if (isDirty) {
      setIsConfirmOpened(true)
    } else onClose()
  }

  useEffect(() => {
    if (flightDetail?.avnumap && getValues('rmdate') && flightDetail?.rmdoc) onFuelsChange()
  }, [flightDetail])

  useEffect(() => {
    if (flightDetail && flightDetail?.rmdoc && !creating) {
      ;(async () => {
        const flightNumber = ('0' + flightDetail?.voord).slice(-2)
        const rmDateArray = getValues('rmdate') as number[]
        const workorders = await fetchWorkorders({
          lang: lang,
          avnumap: flightDetail?.avnumap || null,
          rmdate: rmDateArray
            ? tools.convertDatePformatToYyyySepMmSepJj(tools.convertArrayDateToPformat(rmDateArray), '-')
            : null,
          rmdoc: flightDetail?.rmdoc,
          voord: flightNumber,
        })
        setWorkorders(workorders?.data)
      })()
    }
  }, [flightDetail])

  useEffect(() => {
    loadOptionsFlightTypes({ column: 'A' }, 'votycod', 'votylib', 'votylib', true, false)
    loadOptionsDelayCodes({ column: 'A' }, 'rmrtcod', 'rmrtlib', 'rmrtlib')
  }, [])

  return (
    <>
      {!readOnly && (
        <Stepper
          nonLinear
          activeStep={activeStep}
          horizontal
          style={{ marginTop: '2ex' }}
          onClick={(index) => handleGotoStep(index)}
        >
          {steps.map((label, stepIndex) => (
            <Step key={`techlog_step_${stepIndex}`}>
              <StepHeader>{label}</StepHeader>
            </Step>
          ))}
        </Stepper>
      )}

      <FormProvider {...methods}>
        <form onSubmit={handleSubmit(onSubmit, onError)} style={{ marginTop: '1em' }}>
          {(readOnly || [0, 2].includes(activeStep)) && (
            <>
              <Fieldset legend={t('9500161')}>
                <Stack spacing='2-x'>
                  <Row alignItems='center' justify='space-between' style={{ padding: '0 2rem' }}>
                    <Col xxs={3} style={{ paddingRight: '10px' }}>
                      <FormDate
                        label={t('9500162')}
                        name='_rmdate'
                        rules={{
                          required: true,
                          validate: isValid,
                        }}
                        error={Boolean(errors?._rmdate)}
                        readOnly={readOnly || activeStep === 2 || displayMode !== DisplayModus.CREATE}
                      />
                    </Col>
                    <Col xxs={3} style={{ paddingRight: '10px' }}>
                      <FormInput
                        label={t('7002927')}
                        name='vovolnum'
                        placeholder={t('7002927') as string}
                        error={Boolean(errors?.vovolnum)}
                        maxLength={5}
                        capitalize
                        readOnly={readOnly || activeStep === 2}
                      />
                    </Col>
                    <Col xxs={4} style={{ paddingRight: '10px' }}>
                      <FormSelect
                        label={t('1MRM097')}
                        name='votycod'
                        options={flightTypeOptions}
                        error={Boolean(errors?.votycod)}
                        readOnly={readOnly || activeStep === 2}
                        getOptionLabel={renderOptionLabel}
                      />
                    </Col>
                    <Col xxs={2} style={{ paddingRight: '10px' }}>
                      <FormToggle
                        label={t('0MRM030')}
                        name='voetops'
                        defaultChecked={false}
                        disabled={readOnly || activeStep === 2}
                      />
                    </Col>
                  </Row>
                  <Row justify='space-between' style={{ padding: '0 2rem' }}>
                    <Col xxs={3} style={{ paddingRight: '10px' }}>
                      <FormInput
                        label={t('9420175')}
                        name='voaerodep'
                        placeholder={t('9420175') as string}
                        maxLength={4}
                        readOnly
                      />
                    </Col>
                    <Col xxs={3} style={{ paddingRight: '10px' }}>
                      <FormInput
                        label={t('9420176')}
                        name='voaeroarr'
                        placeholder={t('9420176') as string}
                        rules={{
                          required: true,
                        }}
                        error={Boolean(errors?.voaeroarr)}
                        maxLength={4}
                        capitalize
                        readOnly={(editing && !isLast) || readOnly || activeStep === 2}
                      />
                    </Col>
                    <Col xxs={3} style={{ paddingRight: '10px' }}>
                      {formatDuration === SAISIE_HORAIRE.HEURES_CENTIEMES ? (
                        <FormNumericInput
                          label={t('9500158')}
                          name='voabtpsc'
                          placeholder={t('placeholder_techlogs_duration_heures_centiemes')}
                          rules={{
                            required: true,
                          }}
                          minValue={0}
                          maxValue={99.99}
                          step={0.01}
                          error={Boolean(errors.voabtpsc)}
                          readOnly={(editing && !isLast) || readOnly || activeStep === 2}
                        />
                      ) : (
                        <FormTime
                          label={t('9500158')}
                          name='voabhhmm'
                          rules={{
                            required: true,
                          }}
                          error={Boolean(errors.voabhhmm)}
                          rules={{ required: true, validate: (d: string) => isValid(parse(d, 'HH:mm', new Date())) }}
                          readOnly={(editing && !isLast) || readOnly || activeStep === 2}
                        />
                      )}
                    </Col>
                    {canInputBB && (
                      <Col xxs={3} style={{ paddingRight: '10px' }}>
                        {formatDuration === SAISIE_HORAIRE.HEURES_CENTIEMES ? (
                          <FormNumericInput
                            label={t('9500185')}
                            name='vobbtpsc'
                            placeholder={t('placeholder_techlogs_duration_heures_centiemes')}
                            rules={{
                              required: true,
                            }}
                            minValue={0}
                            maxValue={99.99}
                            step={0.01}
                            error={Boolean(errors.vobbtpsc)}
                            readOnly={(editing && !isLast) || readOnly || activeStep === 2}
                          />
                        ) : (
                          <FormTime
                            label={t('9500185')}
                            name='vobbhhmm'
                            rules={{
                              required: true,
                            }}
                            error={Boolean(errors.vobbhhmm)}
                            rules={{ required: true, validate: (d: string) => isValid(parse(d, 'HH:mm', new Date())) }}
                            readOnly={(editing && !isLast) || readOnly || activeStep === 2}
                          />
                        )}
                      </Col>
                    )}
                  </Row>
                  <Row justify='space-between' style={{ padding: '0 2rem' }}>
                    {canInputBB && canInputDepArr && (
                      <Col xxs={3} style={{ paddingRight: '10px' }}>
                        <FormTime
                          label={t('1MRM027')}
                          name='vobbdep'
                          rules={{
                            required: true,
                          }}
                          error={Boolean(errors.vobbdep)}
                          rules={{ required: true, validate: (d: string) => isValid(parse(d, 'HH:mm', new Date())) }}
                          readOnly={(editing && !isLast) || readOnly || activeStep === 2}
                        />
                      </Col>
                    )}
                    {canInputDepArr && (
                      <Col xxs={3} style={{ paddingRight: '10px' }}>
                        <FormTime
                          label={t('1MRM026')}
                          name='voabdep'
                          rules={{
                            required: true,
                          }}
                          error={Boolean(errors.voabdep)}
                          rules={{ required: true, validate: (d: string) => isValid(parse(d, 'HH:mm', new Date())) }}
                          readOnly={(editing && !isLast) || readOnly || activeStep === 2}
                        />
                      </Col>
                    )}
                    {canInputDepArr && (
                      <Col xxs={3} style={{ paddingRight: '10px' }}>
                        <FormTime
                          label={t('1MRM014')}
                          name='voabarr'
                          error={Boolean(errors.voabarr)}
                          rules={{ validate: (d: string) => isValid(parse(d, 'HH:mm', new Date())) }}
                          readOnly={(editing && !isLast) || readOnly || activeStep === 2}
                        />
                      </Col>
                    )}
                    {canInputBB && canInputDepArr && (
                      <Col xxs={3} style={{ paddingRight: '10px' }}>
                        <FormTime
                          label={t('1MRM015')}
                          name='vobbarr'
                          error={Boolean(errors.vobbarr)}
                          rules={{ validate: (d: string) => isValid(parse(d, 'HH:mm', new Date())) }}
                          readOnly={(editing && !isLast) || readOnly || activeStep === 2}
                        />
                      </Col>
                    )}
                  </Row>
                </Stack>
              </Fieldset>

              <Fieldset legend={t('7002855')}>
                <Row justify='space-between' style={{ padding: '0 2rem' }}>
                  {!creating && formatDuration === SAISIE_HORAIRE.HEURES_CENTIEMES && (
                    <Col xxs={2}>
                      <FormNumericInput
                        label={t('9500158')}
                        name='voabtpsc'
                        placeholder={t('9500158') as string}
                        minValue={0}
                        maxValue={9999}
                        step={1}
                        readOnly
                      />
                    </Col>
                  )}
                  {!creating && formatDuration !== SAISIE_HORAIRE.HEURES_CENTIEMES && (
                    <Col xxs={2}>
                      <FormTime label={t('9500158')} name='voabhhmm' readOnly />
                    </Col>
                  )}
                  {!creating && (
                    <Col xxs={2}>
                      <FormInput label={t('0MRM024')} name='vocyc' placeholder={t('0MRM024') as string} readOnly />
                    </Col>
                  )}
                  <Col xxs={readOnly || activeStep === 2 ? 1 : 2}>
                    <FormNumericInput
                      label={t('7000645')}
                      name='votogo'
                      placeholder={t('7000645') as string}
                      minValue={0}
                      maxValue={99}
                      step={1}
                      readOnly={(editing && !isLast) || readOnly || activeStep === 2}
                    />
                  </Col>
                </Row>

                <NonStandardAgeingDetails
                  rmdate={watch('_rmdate') as Date}
                  formatDuration={formatDuration}
                  nonStandardAgeingDetails={nonStandardAgeingDetails}
                  readOnly={(editing && !isLast) || readOnly}
                  activeStep={activeStep}
                  errors={errors}
                />
              </Fieldset>

              <Fieldset legend={t('9510053')}>
                {!creating && (
                  <>
                    <div style={{ display: canCreateWorkorder ? 'visible' : 'none' }}>
                      <IconButton
                        size='small'
                        onClick={handleCreateWorkOrder}
                        variant='ghost'
                        style={{ marginBottom: '1em' }}
                      >
                        <FontAwesomeIcon icon={faSquarePlus} />
                      </IconButton>
                    </div>
                    <TechlogWorkorders workorders={workorders} isDisplayMode={readOnly} />
                  </>
                )}
              </Fieldset>

              <Fieldset legend={t('0MRM082')}>
                <Row justify='space-between' style={{ padding: '0 2rem' }}>
                  <Col xxs={6} style={{ paddingRight: '10px' }}>
                    <FormSelect
                      label={t('0MRM019')}
                      name='vortdcod1'
                      rules={{ required: getValues('vortdtps1') || getValues('voann1') }}
                      readOnly={readOnly || activeStep === 2}
                      options={delayCodeOptions}
                      error={Boolean(errors?.vortdcod1)}
                      getOptionLabel={renderOptionLabel}
                    />
                  </Col>
                  <Col xxs={3} style={{ paddingRight: '10px' }}>
                    <FormNumericInput
                      label={t('7000027')}
                      name='vortdtps1'
                      minValue={0}
                      maxValue={999999}
                      step={1}
                      rules={{ required: getValues('vortdcod1') || getValues('voann1') }}
                      placeholder={t('7000027') as string}
                      readOnly={readOnly || activeStep === 2}
                    />
                  </Col>
                  <Col xxs={3} style={{ paddingRight: '10px' }}>
                    <FormToggle
                      label={t('7009267')}
                      name='voann1'
                      defaultChecked={false}
                      disabled={readOnly || activeStep === 2}
                    />
                  </Col>
                </Row>
                <Row justify='space-between' style={{ padding: '0 2rem' }}>
                  <Col xxs={6} style={{ paddingRight: '10px' }}>
                    <FormSelect
                      label={t('0MRM019')}
                      name='vortdcod2'
                      rules={{ required: getValues('vortdtps2') || getValues('voann2') }}
                      readOnly={readOnly || activeStep === 2}
                      options={delayCodeOptions}
                      error={Boolean(errors?.vortdcod2)}
                      getOptionLabel={renderOptionLabel}
                    />
                  </Col>
                  <Col xxs={3} style={{ paddingRight: '10px' }}>
                    <FormNumericInput
                      label={t('7000027')}
                      name='vortdtps2'
                      minValue={0}
                      maxValue={999999}
                      step={1}
                      rules={{ required: getValues('vortdcod2') || getValues('voann2') }}
                      placeholder={t('7000027') as string}
                      readOnly={readOnly || activeStep === 2}
                    />
                  </Col>
                  <Col xxs={3} style={{ paddingRight: '10px' }}>
                    <FormToggle
                      label={t('7009267')}
                      name='voann2'
                      defaultChecked={false}
                      disabled={readOnly || activeStep === 2}
                    />
                  </Col>
                </Row>
              </Fieldset>
            </>
          )}

          {(readOnly || [1, 2].includes(activeStep)) && (
            <>
              <Fieldset legend={t('9500163')}>
                <Fieldset legend={t('0MRM025')}>
                  <Inline spacing='2-x' style={{ padding: '0 2rem' }}>
                    {Array.from({ length: nbEngines | 0 }, (_, i) => i + 1).map((i) => (
                      <FormNumericInput
                        label={t('7002298') + ' ' + i}
                        name={'vooilmo' + i + 'd'}
                        key={'vooilmo' + i + 'd'}
                        placeholder={t('placeholder_techlogs_engine') + ' ' + i}
                        minValue={0}
                        maxValue={99.9}
                        step={0.1}
                        readOnly={readOnly || activeStep === 2}
                      />
                    ))}
                    {Array.from({ length: nbApu | 0 }, (_, i) => i + 1).map((i) => (
                      <FormNumericInput
                        label={t('0MRM010') + ' ' + i}
                        name={'vooilap' + i + 'd'}
                        key={'vooilap' + i + 'd'}
                        placeholder={t('placeholder_techlogs_engine_apu') + ' ' + i}
                        minValue={0}
                        maxValue={99.9}
                        step={0.1}
                        readOnly={readOnly || activeStep === 2}
                      />
                    ))}
                  </Inline>
                </Fieldset>
                <Fieldset legend={t('1MRM013')}>
                  <Inline spacing='2-x' style={{ padding: '0 2rem' }}>
                    {Array.from({ length: nbEngines | 0 }, (_, i) => i + 1).map((i) => (
                      <FormNumericInput
                        label={t('7002298') + ' ' + i}
                        name={'vooilmo' + i + 'a'}
                        key={'vooilmo' + i + 'a'}
                        placeholder={t('placeholder_techlogs_engine') + ' ' + i}
                        minValue={0}
                        maxValue={99.9}
                        step={0.1}
                        readOnly={readOnly || activeStep === 2}
                      />
                    ))}
                    {Array.from({ length: nbApu | 0 }, (_, i) => i + 1).map((i) => (
                      <FormNumericInput
                        label={t('0MRM010') + ' ' + i}
                        name={'vooilap' + i + 'a'}
                        key={'vooilap' + i + 'a'}
                        placeholder={t('placeholder_techlogs_engine_apu') + ' ' + i}
                        minValue={0}
                        maxValue={99.9}
                        step={0.1}
                        readOnly={readOnly || activeStep === 2}
                      />
                    ))}
                  </Inline>
                </Fieldset>
              </Fieldset>

              <Fieldset legend={t('9500171')}>
                <Fieldset legend={t('0MRM025')}>
                  <Inline spacing='2-x' style={{ padding: '0 2rem' }}>
                    {Array.from({ length: nbHydro | 0 }, (_, i) => i + 1).map((i) => (
                      <FormNumericInput
                        label={t('9300107') + ' ' + i}
                        name={'vooilhy' + i + 'd'}
                        key={'vooilhy' + i + 'd'}
                        placeholder={t('placeholder_techlogs_hydro') + ' ' + i}
                        minValue={0}
                        maxValue={9.9}
                        step={0.1}
                        readOnly={readOnly || activeStep === 2}
                      />
                    ))}
                  </Inline>
                </Fieldset>
                <Fieldset legend={t('1MRM013')}>
                  <Inline spacing='2-x' style={{ padding: '0 2rem' }}>
                    {Array.from({ length: nbHydro | 0 }, (_, i) => i + 1).map((i) => (
                      <FormNumericInput
                        label={t('9300107') + ' ' + i}
                        name={'vooilhy' + i + 'a'}
                        key={'vooilhy' + i + 'a'}
                        placeholder={t('placeholder_techlogs_hydro') + ' ' + i}
                        minValue={0}
                        maxValue={9.9}
                        step={0.1}
                        readOnly={readOnly || activeStep === 2}
                      />
                    ))}
                  </Inline>
                </Fieldset>
              </Fieldset>

              <Fieldset legend={t('1MRM018')}>
                {canManageFuel && !creating ? (
                  <TechlogFuels
                    lang={lang}
                    rmdate={tools.convertArrayDateToPformat(getValues('rmdate') as number[])}
                    avnumap={flightDetail?.avnumap || null}
                    rmdoc={flightDetail?.rmdoc}
                    voord={flightDetail?.voord}
                    displayMode={displayMode}
                    activeStep={activeStep}
                    canManageFuel={canManageFuel}
                    setModalFuelOpened={setModalFuelOpened}
                    setModalFormFuelDisplayMode={setModalFormFuelDisplayMode}
                    setSelectedTechlogFuel={setSelectedTechlogFuel}
                    fuelsDetail={fuelsDetail}
                    onFuelsChange={onFuelsChange}
                  />
                ) : creating ? (
                  <Typography>{t('message_access_after_creation_crm')}</Typography>
                ) : (
                  <Typography>{t('9510061')}</Typography>
                )}

                {canViewTotals && !creating && (
                  <Inline alignY='start' spacing='2-x' style={{ marginLeft: '2rem', marginTop: '1rem' }}>
                    <FormNumericInput
                      label={t('9160327')}
                      name='ketcatd'
                      placeholder={t('9160327')}
                      minValue={0}
                      step={1}
                      readOnly
                    />
                    <FormNumericInput
                      label={t('9160330')}
                      name='ketcata'
                      placeholder={t('9160330')}
                      minValue={0}
                      maxValue={100000}
                      step={1}
                      readOnly={readOnly || activeStep === 2}
                    />
                    <FormNumericInput
                      label={t('9160331')}
                      name='ketcatp'
                      placeholder={t('9160331')}
                      minValue={0}
                      maxValue={100000}
                      step={1}
                      readOnly={readOnly || activeStep === 2}
                    />
                  </Inline>
                )}
              </Fieldset>

              {creating && (
                <Fieldset legend={t('9510053')}>
                  <Typography>{t('message_access_after_creation_crm')}</Typography>
                </Fieldset>
              )}
            </>
          )}

          {!readOnly && (
            <Inline spacing='2-x' style={{ width: '100%', margin: '50px 0' }}>
              <Button variant='secondary' onClick={handleClose}>
                {t('8000569')}
              </Button>
              <Button
                variant='primary'
                type='submit'
                disabled={activeStep !== 2 || isLoading || !isDirty || !canUpdate}
              >
                {t('9500174')}
              </Button>
            </Inline>
          )}
        </form>
      </FormProvider>
      <ModalTechlogFuel
        lang={lang}
        open={modalFuelOpened}
        displayMode={modalFormFuelDisplayMode}
        onClose={() => setModalFuelOpened(false)}
        techlogFuel={selectedTechlogFuel}
        onPostSubmit={handleModalFormFuelPostSubmit}
      />

      <ModalConfirm
        confirmMessage={t('GEN0090')}
        isOpened={isConfirmOpened}
        onCloseModal={() => setIsConfirmOpened(false)}
        onSubmit={handleConfirmSubmit}
      />
    </>
  )
}

export default TechlogForm
